import { Form, FormAnswer, PostSubmitConfig } from '../types'

/** Configuración por defecto: se conserva el comportamiento actual. */
export const DEFAULT_POST_SUBMIT: PostSubmitConfig = {
  action: 'gracias',
  whatsappAuto: false,
  whatsappLabel: 'Hablar por WhatsApp',
  whatsappMessage:
    'Hola, soy {{full_name}} de {{company}}. Acabo de completar el formulario y quiero recibir más información.',
  messageTitle: 'Solicitud enviada correctamente',
  messageText:
    'Hemos recibido tus datos y nos pondremos en contacto contigo.',
}

export function postSubmitConfig(form: Form): PostSubmitConfig {
  return { ...DEFAULT_POST_SUBMIT, ...(form.postSubmit ?? {}) }
}

/** Una URL externa solo se usa si es http(s) y se puede analizar. */
export function isValidUrl(url?: string): boolean {
  if (!url) return false
  try {
    const parsed = new URL(url.trim())
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

/** wa.me exige solo dígitos: sin +, espacios, guiones ni paréntesis. */
export function whatsappDigits(raw?: string): string {
  return (raw ?? '').replace(/\D/g, '')
}

/**
 * Sustituye las variables del mensaje con los datos que acaba de dejar el
 * visitante. Las variables sin valor se eliminan en vez de quedar visibles.
 */
export function fillTemplate(
  template: string,
  contact: Record<string, string>,
  answers: FormAnswer[],
): string {
  const name = contact.full_name ?? contact.name ?? contact.firstName ?? ''
  const company = contact.company ?? contact.company_name ?? ''
  const values: Record<string, string> = {
    full_name: name,
    name,
    nombre: name,
    company,
    company_name: company,
    empresa: company,
    phone: contact.phone ?? '',
    telefono: contact.phone ?? '',
    email: contact.email ?? '',
    correo: contact.email ?? '',
  }
  /**
   * Alias legibles por tipo de pregunta. Se reconocen por palabras clave de la
   * etiqueta, así que el usuario puede reescribir el texto visible de la
   * pregunta sin romper el mensaje.
   */
  const ALIASES: Array<[string, RegExp]> = [
    ['ciudad', /ubicaci|donde se encuentra|ciudad|pais/],
    ['instagram', /instagram/],
    ['descripcion', /describe|se dedica|actividad/],
    ['servicio', /trabajar con nosotros|tipo de servicio|servicio/],
    ['publicidad', /publicidad|inviertes|inversion/],
    ['presupuesto', /presupuesto/],
    ['comentarios', /algo mas|comentario/],
  ]
  answers.forEach((answer) => {
    const value = Array.isArray(answer.value)
      ? answer.value.join(', ')
      : String(answer.value ?? '')
    values[answer.questionId] = value
    // Etiqueta normalizada, para poder escribir {{presupuesto_disponible}}.
    const key = answer.label
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '')
    if (key) values[key] = value
    const plain = answer.label
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
    ALIASES.forEach(([alias, pattern]) => {
      if (!values[alias] && pattern.test(plain)) values[alias] = value
    })
  })
  return template
    .replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_match, name: string) => values[name] ?? '')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

/**
 * Elimina las líneas cuyas variables han quedado vacías. Así el mensaje nunca
 * contiene renglones absurdos del tipo «Empresa:» o «Ciudad:».
 */
export function fillMessage(
  template: string,
  contact: Record<string, string>,
  answers: FormAnswer[],
): string {
  return template
    .split('\n')
    .map((line) => {
      const filled = fillTemplate(line, contact, answers)
      // La línea tenía variables y todas quedaron vacías: se descarta.
      if (/\{\{/.test(line)) {
        const withoutLabel = filled.replace(/^[^:]{1,40}:\s*/, '').trim()
        if (withoutLabel === '') return null
      }
      return filled
    })
    .filter((line): line is string => line !== null)
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

export interface StoredSubmission {
  contact: Record<string, string>
  answers: FormAnswer[]
  at: number
}

const SUBMISSION_KEY = 'ava:submission:'

/**
 * Guarda el envío que acaba de completar ESTE visitante, en sessionStorage
 * (aislado por pestaña). La página de gracias usa exclusivamente este dato:
 * nunca consulta «el último lead» de la organización, así que no puede mezclar
 * los datos de dos visitantes.
 */
export function storeSubmission(
  slug: string,
  data: { contact: Record<string, string>; answers: FormAnswer[] },
): void {
  try {
    sessionStorage.setItem(
      SUBMISSION_KEY + slug,
      JSON.stringify({ ...data, at: Date.now() }),
    )
  } catch {
    // Si el navegador bloquea el almacenamiento, la página de gracias
    // simplemente mostrará el mensaje sin personalizar.
  }
}

export function readSubmission(slug: string): StoredSubmission | null {
  try {
    const raw = sessionStorage.getItem(SUBMISSION_KEY + slug)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredSubmission
    if (!parsed || typeof parsed !== 'object') return null
    return parsed
  } catch {
    return null
  }
}

/** Construye el enlace https://wa.me/NUMERO?text=MENSAJE_CODIFICADO */
export function whatsappLink(
  config: PostSubmitConfig,
  contact: Record<string, string>,
  answers: FormAnswer[],
): string | null {
  const number = whatsappDigits(config.whatsappNumber)
  if (!number) return null
  const text = fillMessage(config.whatsappMessage ?? '', contact, answers)
  return text
    ? `https://wa.me/${number}?text=${encodeURIComponent(text)}`
    : `https://wa.me/${number}`
}
